/**
 * Equipment definitions
 * Rods, Reels, Tackle, Bait, and Lines
 */

import { EquipmentDefinition, EquipmentSlot } from '@/types';

/**
 * All equipment items in the game
 */
export const EQUIPMENT_DEFINITIONS: EquipmentDefinition[] = [
  // ========================================
  // RODS
  // ========================================
  {
    id: 'rod_wooden',
    name: 'Wooden Rod',
    slot: EquipmentSlot.ROD,
    tier: 1,
    cost: 0, // Starting equipment
    stats: {
      fishingSpeed: 0,
      catchChance: 0,
      qualityBonus: 0,
    },
    description: 'A basic wooden fishing rod. Better than nothing!',
    icon: '🎣',
  },
  {
    id: 'rod_reinforced',
    name: 'Reinforced Rod',
    slot: EquipmentSlot.ROD,
    tier: 2,
    cost: 50,
    stats: {
      fishingSpeed: 0.1, // 10% faster
      catchChance: 0.05, // +5% catch rate
      qualityBonus: 0,
    },
    requires: {
      previousTier: 'rod_wooden',
    },
    description: 'A sturdier rod with better casting action.',
    icon: '🎣',
  },
  {
    id: 'rod_fiberglass',
    name: 'Fiberglass Rod',
    slot: EquipmentSlot.ROD,
    tier: 3,
    cost: 150,
    stats: {
      fishingSpeed: 0.2, // 20% faster
      catchChance: 0.1, // +10% catch rate
      qualityBonus: 0.05, // +5% quality chance
    },
    requires: {
      previousTier: 'rod_reinforced',
    },
    description: 'Modern fiberglass rod with excellent sensitivity.',
    icon: '🎣',
  },

  // ========================================
  // REELS
  // ========================================
  {
    id: 'reel_basic',
    name: 'Basic Reel',
    slot: EquipmentSlot.REEL,
    tier: 1,
    cost: 0, // Starting equipment
    stats: {
      fishingSpeed: 0,
      catchChance: 0,
    },
    description: 'A simple spinning reel.',
    icon: '⚙️',
  },
  {
    id: 'reel_spinning',
    name: 'Spinning Reel',
    slot: EquipmentSlot.REEL,
    tier: 2,
    cost: 75,
    stats: {
      fishingSpeed: 0.08, // 8% faster
      catchChance: 0.03, // +3% catch rate
    },
    requires: {
      previousTier: 'reel_basic',
    },
    description: 'Smooth casting and retrieval.',
    icon: '⚙️',
  },
  {
    id: 'reel_baitcaster',
    name: 'Baitcasting Reel',
    slot: EquipmentSlot.REEL,
    tier: 3,
    cost: 200,
    stats: {
      fishingSpeed: 0.15, // 15% faster
      catchChance: 0.08, // +8% catch rate
      qualityBonus: 0.03, // +3% quality
    },
    requires: {
      previousTier: 'reel_spinning',
    },
    description: 'Professional-grade precision reel.',
    icon: '⚙️',
  },

  // ========================================
  // BAIT (consumable — consumed per catch)
  // ========================================
  {
    id: 'bait_worms',
    name: 'Worms',
    slot: EquipmentSlot.BAIT,
    tier: 1,
    cost: 15, // Per stack of 10
    stats: {
      catchChance: 0.05, // +5% catch rate
    },
    description: 'Classic bait. Consumed on each catch.',
    icon: '🪱',
    isConsumable: true,
    purchaseQuantity: 20,
  },
  {
    id: 'bait_crickets',
    name: 'Crickets',
    slot: EquipmentSlot.BAIT,
    tier: 1,
    cost: 25, // Per stack of 20
    stats: {
      catchChance: 0.04, // +4% catch rate
      fishingSpeed: 0.06, // +6% speed
    },
    description: 'Lively bait that speeds up fishing.',
    icon: '🦗',
    isConsumable: true,
    purchaseQuantity: 20,
  },
  {
    id: 'bait_minnows',
    name: 'Live Minnows',
    slot: EquipmentSlot.BAIT,
    tier: 2,
    cost: 40, // Per stack of 20
    stats: {
      catchChance: 0.08, // +8% catch rate
      qualityBonus: 0.12, // +12% quality
    },
    description: 'Premium live bait that attracts bigger fish.',
    icon: '🐟',
    isConsumable: true,
    purchaseQuantity: 20,
  },

  // ========================================
  // LINES
  // ========================================
  {
    id: 'line_mono',
    name: 'Monofilament Line',
    slot: EquipmentSlot.LINE,
    tier: 1,
    cost: 0, // Starting equipment
    stats: {},
    description: 'Standard monofilament fishing line.',
    icon: '🧵',
  },
  {
    id: 'line_braided',
    name: 'Braided Line',
    slot: EquipmentSlot.LINE,
    tier: 2,
    cost: 80,
    stats: {
      fishingSpeed: 0.05, // 5% faster
      catchChance: 0.04, // +4% catch rate
    },
    requires: {
      previousTier: 'line_mono',
    },
    description: 'Strong braided line with better sensitivity and less stretch.',
    icon: '🧵',
  },

  // ========================================
  // TACKLE (bobbers, weights, lures)
  // ========================================
  {
    id: 'bobber_basic',
    name: 'Basic Bobber',
    slot: EquipmentSlot.TACKLE,
    tier: 1,
    cost: 0, // Starting equipment
    stats: {
      catchChance: 0.02, // +2% catch rate
    },
    description: 'A simple float bobber. Enables bait use.',
    icon: '🟡',
    tackleType: 'bobber',
  },
  {
    id: 'bobber_slip',
    name: 'Slip Bobber',
    slot: EquipmentSlot.TACKLE,
    tier: 2,
    cost: 60,
    stats: {
      catchChance: 0.05, // +5% catch rate
    },
    description: 'Adjustable slip bobber for precise depth. Enables bait use.',
    icon: '🔴',
    tackleType: 'bobber',
  },
  {
    id: 'weight_split',
    name: 'Split Shot',
    slot: EquipmentSlot.TACKLE,
    tier: 1,
    cost: 30,
    stats: {
      fishingSpeed: 0.05, // +5% speed
    },
    description: 'Small lead weights for faster sinking. Enables bait use.',
    icon: '⚫',
    tackleType: 'weight',
  },
  {
    id: 'weight_sinker',
    name: 'Bell Sinker',
    slot: EquipmentSlot.TACKLE,
    tier: 2,
    cost: 80,
    stats: {
      fishingSpeed: 0.08, // +8% speed
      catchChance: 0.03, // +3% catch rate
    },
    description: 'Heavy sinker for deep water fishing. Enables bait use.',
    icon: '🔔',
    tackleType: 'weight',
  },
  {
    id: 'lure_spinner',
    name: 'Spinner',
    slot: EquipmentSlot.TACKLE,
    tier: 1,
    cost: 0, // Free alternative to bobber
    stats: {
      fishingSpeed: 0.03, // +3% speed
      catchChance: 0.03, // +3% catch rate
    },
    description: 'Metal spinner lure. Cannot use bait.',
    icon: '🎯',
    tackleType: 'lure',
  },
  {
    id: 'lure_crankbait',
    name: 'Crankbait',
    slot: EquipmentSlot.TACKLE,
    tier: 2,
    cost: 120,
    stats: {
      qualityBonus: 0.06, // +6% quality
      catchChance: 0.03, // +3% catch rate
    },
    description: 'Diving crankbait for quality fish. Cannot use bait.',
    icon: '🎯',
    tackleType: 'lure',
  },

  // ========================================
  // FLY RODS (triggers fly cast minigame)
  // ========================================
  {
    id: 'rod_fly_bamboo',
    name: 'Bamboo Fly Rod',
    slot: EquipmentSlot.ROD,
    tier: 2,
    cost: 120,
    stats: {
      fishingSpeed: 0.05,    // +5% speed (lower than conventional tier 2)
      catchChance: 0.03,     // +3% catch (minigame bonuses compensate)
      qualityBonus: 0.08,    // +8% quality (higher than conventional)
    },
    requires: {
      previousTier: 'rod_wooden',
    },
    description: 'A classic bamboo fly rod. Enables the fly casting minigame for skillful anglers.',
    icon: '🪶',
    isFlyRod: true,
  },
  {
    id: 'rod_fly_graphite',
    name: 'Graphite Fly Rod',
    slot: EquipmentSlot.ROD,
    tier: 3,
    cost: 250,
    stats: {
      fishingSpeed: 0.12,    // +12% speed
      catchChance: 0.05,     // +5% catch
      qualityBonus: 0.15,    // +15% quality (much higher than conventional)
    },
    requires: {
      previousTier: 'rod_fly_bamboo',
    },
    description: 'High-performance graphite fly rod with exceptional sensitivity and casting control.',
    icon: '🪶',
    isFlyRod: true,
  },
  {
    id: 'rod_fly_carbon',
    name: 'Carbon Fiber Fly Rod',
    slot: EquipmentSlot.ROD,
    tier: 4,
    cost: 500,
    stats: {
      fishingSpeed: 0.18,    // +18% speed
      catchChance: 0.08,     // +8% catch
      qualityBonus: 0.22,    // +22% quality (best quality rod in game)
    },
    requires: {
      previousTier: 'rod_fly_graphite',
    },
    description: 'The ultimate fly rod. Ultra-light carbon fiber delivers perfect cast control.',
    icon: '🪶',
    isFlyRod: true,
  },

  // ========================================
  // FLY PATTERNS (consumable — used like bait, for fly rods)
  // ========================================
  {
    id: 'fly_adams',
    name: 'Adams Dry Fly',
    slot: EquipmentSlot.BAIT,
    tier: 1,
    cost: 15,
    stats: {
      catchChance: 0.03,
      qualityBonus: 0.05,
    },
    description: 'A versatile dry fly. Targets trout species. Good casts preserve the fly!',
    icon: '🪶',
    isConsumable: true,
    purchaseQuantity: 8,
    flyPatternId: 'pattern_adams',
  },
  {
    id: 'fly_woolly_bugger',
    name: 'Woolly Bugger',
    slot: EquipmentSlot.BAIT,
    tier: 1,
    cost: 18,
    stats: {
      catchChance: 0.05,
      qualityBonus: 0.03,
    },
    description: 'An aggressive streamer fly. Targets bass and predators. Good casts preserve the fly!',
    icon: '🪶',
    isConsumable: true,
    purchaseQuantity: 8,
    flyPatternId: 'pattern_woolly_bugger',
  },
  {
    id: 'fly_elk_hair_caddis',
    name: 'Elk Hair Caddis',
    slot: EquipmentSlot.BAIT,
    tier: 1,
    cost: 16,
    stats: {
      catchChance: 0.04,
      qualityBonus: 0.04,
    },
    description: 'A buoyant caddis imitation. Targets panfish and trout. Good casts preserve the fly!',
    icon: '🪶',
    isConsumable: true,
    purchaseQuantity: 8,
    flyPatternId: 'pattern_elk_hair_caddis',
  },
  {
    id: 'fly_royal_wulff',
    name: 'Royal Wulff',
    slot: EquipmentSlot.BAIT,
    tier: 2,
    cost: 30,
    stats: {
      catchChance: 0.06,
      qualityBonus: 0.10,
    },
    description: 'A premium attractor pattern. Targets rare species. Good casts preserve the fly!',
    icon: '🪶',
    isConsumable: true,
    purchaseQuantity: 5,
    flyPatternId: 'pattern_royal_wulff',
  },

  // ========================================
  // TRAPS (passive trapping — Set → Soak → Pull)
  // ========================================
  {
    id: 'trap_wire_basket',
    name: 'Wire Basket Trap',
    slot: EquipmentSlot.TRAP,
    tier: 1,
    cost: 75,
    stats: {},
    description: 'A simple wire mesh trap. 2 min soak, catches 3-5 creatures.',
    icon: '🪤',
    trapSoakMs: 2 * 60 * 1000,   // 2 minutes
    trapYieldMin: 3,
    trapYieldMax: 5,
  },
  {
    id: 'trap_crawdad_pot',
    name: 'Crawdad Pot',
    slot: EquipmentSlot.TRAP,
    tier: 2,
    cost: 200,
    stats: {
      qualityBonus: 0.05, // +5% creature quality
    },
    description: 'A proper crayfish pot with funnel entry. 3 min soak, catches 4-8 creatures.',
    icon: '🪤',
    trapSoakMs: 3 * 60 * 1000,   // 3 minutes
    trapYieldMin: 4,
    trapYieldMax: 8,
  },
  {
    id: 'trap_deluxe_pot',
    name: 'Deluxe Pot',
    slot: EquipmentSlot.TRAP,
    tier: 3,
    cost: 450,
    stats: {
      qualityBonus: 0.10, // +10% creature quality
    },
    description: 'A professional-grade trap with multiple compartments. 5 min soak, catches 6-12 creatures.',
    icon: '🪤',
    trapSoakMs: 5 * 60 * 1000,   // 5 minutes
    trapYieldMin: 6,
    trapYieldMax: 12,
  },

  // ========================================
  // TRAP BAIT (consumable — consumed per trap set)
  // ========================================
  {
    id: 'trap_bait_chicken_liver',
    name: 'Chicken Liver',
    slot: EquipmentSlot.TRAP,
    tier: 1,
    cost: 10,
    stats: {},
    description: 'Cheap bait for traps. +1-2 bonus creatures per pull.',
    icon: '🍖',
    isConsumable: true,
    purchaseQuantity: 10,
    isTrapBait: true,
  },
  {
    id: 'trap_bait_fish_heads',
    name: 'Fish Heads',
    slot: EquipmentSlot.TRAP,
    tier: 1,
    cost: 20,
    stats: {
      qualityBonus: 0.05, // +5% quality when used as trap bait
    },
    description: 'Quality trap bait. +1-2 bonus creatures, better quality.',
    icon: '🐟',
    isConsumable: true,
    purchaseQuantity: 10,
    isTrapBait: true,
  },
  {
    id: 'trap_bait_herring_chunks',
    name: 'Herring Chunks',
    slot: EquipmentSlot.TRAP,
    tier: 2,
    cost: 35,
    stats: {
      qualityBonus: 0.10, // +10% quality when used as trap bait
    },
    description: 'Premium trap bait. +2-3 bonus creatures, much better quality.',
    icon: '🐟',
    isConsumable: true,
    purchaseQuantity: 10,
    isTrapBait: true,
  },
];

/**
 * Equipment lookup by ID (for fast access)
 */
const EQUIPMENT_MAP = new Map(EQUIPMENT_DEFINITIONS.map((eq) => [eq.id, eq]));

/**
 * Get equipment definition by ID
 * @throws Error if equipment not found
 */
export function getEquipmentDefinition(id: string): EquipmentDefinition {
  const equipment = EQUIPMENT_MAP.get(id);
  if (!equipment) {
    throw new Error(`Equipment not found: ${id}`);
  }
  return equipment;
}

/**
 * Get all equipment for a specific slot
 */
export function getEquipmentBySlot(slot: EquipmentSlot): EquipmentDefinition[] {
  return EQUIPMENT_DEFINITIONS.filter((eq) => eq.slot === slot);
}

/**
 * Get all equipment of a specific tier
 */
export function getEquipmentByTier(tier: number): EquipmentDefinition[] {
  return EQUIPMENT_DEFINITIONS.filter((eq) => eq.tier === tier);
}

/**
 * Check if equipment ID exists
 */
export function equipmentExists(id: string): boolean {
  return EQUIPMENT_MAP.has(id);
}

/**
 * Get starting equipment (tier 1, free)
 */
export function getStartingEquipment(): EquipmentDefinition[] {
  return EQUIPMENT_DEFINITIONS.filter((eq) => eq.cost === 0);
}

/**
 * Export as EQUIPMENT_DATA for consistency
 */
export const EQUIPMENT_DATA: Record<string, EquipmentDefinition> = Object.fromEntries(
  EQUIPMENT_DEFINITIONS.map((equipment) => [equipment.id, equipment])
);
